import React, { useEffect, useMemo, useState } from "react";
import Loader from "react-spinner-loader";
import {
  X,
  History as HistoryIcon,
  PlusCircle,
  Pencil,
  Trash2,
  RefreshCcw,
  Repeat,
  ChevronUp,
  Sheet,
} from "lucide-react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getData } from "../../../../Services/Services";
import * as EndPoints from "../../../../Services/EndPoints";

const AUDIT_EXPORT_COLUMNS = [
  "Event ID",
  "Action",
  "Changed By",
  "Changed On",
  "Section",
  "Resource",
  "Available Qty",
  "Damaged Qty",
  "Deployed Qty",
  "Total Qty",
  "Quantity",
  "Previous Quantity",
  "Updated Quantity",
];

const exportFileStamp = () => {
  const p = (n) => String(n).padStart(2, "0");
  const n = new Date();
  return `${p(n.getDate())}-${p(n.getMonth() + 1)}-${n.getFullYear()}_${p(n.getHours())}${p(
    n.getMinutes()
  )}${p(n.getSeconds())}`;
};

const badgeBase = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1.6,
};

const getSeverityBadgeStyle = (severity) => {
  const s = (severity || "").toLowerCase();
  let palette = { background: "#eef2f7", color: "#5b6b7a", borderColor: "#d5e0ea" };
  if (s.includes("minor") || s.includes("level 1")) {
    palette = { background: "#e6f4ea", color: "#1e7e34", borderColor: "#b7e1c1" };
  } else if (s.includes("moderate") || s.includes("level 2")) {
    palette = { background: "#fff4e0", color: "#b26a00", borderColor: "#f2d9a8" };
  } else if (s.includes("major") || s.includes("level 3")) {
    palette = { background: "#ffe9d9", color: "#c2410c", borderColor: "#f3c2a0" };
  } else if (s.includes("critical") || s.includes("level 4")) {
    palette = { background: "#fde5e5", color: "#c0392b", borderColor: "#f2b8b8" };
  }
  return {
    ...badgeBase,
    background: palette.background,
    color: palette.color,
    border: `1px solid ${palette.borderColor}`,
  };
};

// Visual treatment (color / icon) per timeline event action.
const EVENT_META = {
  CREATED: { color: "#1e7e34", bg: "#e6f4ea", border: "#b7e1c1", icon: PlusCircle, label: "Created" },
  CREATE: { color: "#1e7e34", bg: "#e6f4ea", border: "#b7e1c1", icon: PlusCircle, label: "Created" },
  EDIT: { color: "#1d5fa8", bg: "#e7f0fb", border: "#c2d9f0", icon: Pencil, label: "Updated" },
  UPDATE: { color: "#1d5fa8", bg: "#e7f0fb", border: "#c2d9f0", icon: Pencil, label: "Updated" },
  DELETE: { color: "#c0392b", bg: "#fde5e5", border: "#f2b8b8", icon: Trash2, label: "Deleted" },
  DELETED: { color: "#c0392b", bg: "#fde5e5", border: "#f2b8b8", icon: Trash2, label: "Deleted" },
};

const getEventMeta = (action) => {
  const key = (action || "").toUpperCase();
  return (
    EVENT_META[key] || {
      color: "#5b6b7a",
      bg: "#eef2f7",
      border: "#d5e0ea",
      icon: HistoryIcon,
      label: action || "Update",
    }
  );
};

const formatDateTime = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return "NA";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh24 = d.getHours();
  const ampm = hh24 >= 12 ? "PM" : "AM";
  const hh = String(hh24 % 12 || 12).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min} ${ampm}`;
};

const showValue = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  // Defensive fallback: never hand React a raw object to render.
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return "NA";
    }
  }
  return value;
};

// Friendly labels for the quantity fields that can appear inside a resource
// reference (added/removed/unchanged entries, or the from/to of a change).
const FIELD_LABELS = {
  requiredQuantity: "Required Qty",
  availableQuantity: "Available Qty",
  lastRequiredQuantity: "Last Required Qty",
  lastAvailableQuantity: "Last Available Qty",
};

// A resource reference coming back from the API can be a plain name (string)
// or a richer object (e.g. { resourceName, requiredQuantity, cumAvailableQuantity, ... }).
const resourceNameOf = (item) => {
  if (item === null || item === undefined) return "NA";
  if (typeof item !== "object") return String(item);
  return item.resourceName || item.name || "NA";
};

// "Removed" entries carry lastRequiredQuantity instead of requiredQuantity
// (the quantity it had just before removal); everything else uses
// requiredQuantity (or the generic "quantity" used elsewhere in this file).
const resourceQuantityOf = (item) => {
  if (item === null || item === undefined || typeof item !== "object") return null;
  const q = item.requiredQuantity ?? item.lastRequiredQuantity ?? item.quantity;
  return q === undefined ? null : q;
};

// Shared column set for every resource-list block (Created / Added / Removed /
// Unchanged) so they all show the same quantity breakdown, matching the
// Available/Damaged/Deployed/Total/Qty-to-Deploy columns used elsewhere in
// this app's resource tables.
const RESOURCE_DETAIL_COLUMNS = [
  { header: "Resource", cell: (item) => resourceNameOf(item) },
  {
    header: "Available Qty",
    align: "center",
    cell: (item) => showValue(item?.cumAvailableQuantity ?? item?.availableQuantity),
  },
  {
    header: "Damaged Qty",
    align: "center",
    cell: (item) => showValue(item?.cumDamagedQuantity),
  },
  {
    header: "Deployed Qty",
    align: "center",
    cell: (item) => showValue(item?.cumDeployedQuantity),
  },
  {
    header: "Total Qty",
    align: "center",
    cell: (item) => showValue(item?.cumTotalQuantity),
  },
  {
    header: "Quantity",
    align: "center",
    cell: (item) => showValue(resourceQuantityOf(item)),
  },
];

// A "changed" entry's from/to can be a plain scalar (old behaviour: a single
// quantity) or an object carrying one or more quantity fields (new
// behaviour). Normalise both cases into a list of { label, from, to } rows.
const getChangeRows = (change) => {
  const { from, to } = change || {};
  const isObj = (v) => v !== null && typeof v === "object";
  if (!isObj(from) && !isObj(to)) {
    return [{ label: null, from, to }];
  }
  const fromObj = isObj(from) ? from : {};
  const toObj = isObj(to) ? to : {};
  const keys = new Set([...Object.keys(fromObj), ...Object.keys(toObj)]);
  keys.delete("resourceName");
  keys.delete("name");
  const rows = Array.from(keys).map((k) => ({
    label: FIELD_LABELS[k] || k,
    from: fromObj[k],
    to: toObj[k],
  }));
  return rows.length ? rows : [{ label: null, from: "NA", to: "NA" }];
};

const filterLabel = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: ".4px",
  textTransform: "uppercase",
  color: "#5b6b7a",
};

// Color family per kind of change block inside an "Updated" event. Card
// header and table header share the same family so each block reads as one
// unit, and the palettes are distinct enough to scan at a glance.
const SECTION_PALETTE = {
  changes: { color: "#1d5fa8", headBg: "#e7f0fb", cellBg: "#f3f8fd", border: "#c9dcf0" },
  added: { color: "#1e7e34", headBg: "#e6f4ea", cellBg: "#f2f9f4", border: "#bfe0c8" },
  removed: { color: "#c0392b", headBg: "#fde5e5", cellBg: "#fdf3f3", border: "#f2c2c2" },
  unchanged: { color: "#475569", headBg: "#eef1f5", cellBg: "#f7f8fa", border: "#dbe1e8" },
};

// Renders one "block" of an Updated event as its own bordered card: a
// colored title bar (icon + name + row count) sitting above a small table.
// Keeping every kind of change (quantity updates, additions, removals,
// unchanged resources) in this same shape is what makes the event body read
// as a clean, uniform stack instead of a mix of one-off layouts.
const SectionTable = ({ icon: Icon, title, palette, columns, rows, rowKey }) => {
  if (!rows.length) return null;
  return (
    <div
      style={{
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          background: palette.headBg,
        }}
      >
        <Icon size={13} color={palette.color} />
        <span style={{ fontSize: "11.5px", fontWeight: 700, color: palette.color }}>
          {title} - Total {rows.length}
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "360px" }}>
          <thead>
            <tr>
              <th
                style={{
                  width: "50px",
                  textAlign: "center",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: palette.color,
                  padding: "5px 6px",
                  background: palette.cellBg,
                  borderBottom: `1px solid ${palette.border}`,
                }}
              >
                S. No
              </th>
              {columns.map((col) => (
                <th
                  key={col.header}
                  style={{
                    textAlign: col.align || "left",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: palette.color,
                    padding: "5px 8px",
                    background: palette.cellBg,
                    borderBottom: `1px solid ${palette.border}`,
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={rowKey ? rowKey(row, idx) : idx} style={{ background: idx % 2 ? palette.cellBg : "#fff" }}>
                <td
                  style={{
                    textAlign: "center",
                    fontSize: "11px",
                    color: "#8a97a3",
                    padding: "6px",
                    borderBottom: idx === rows.length - 1 ? "none" : "1px solid #f0f2f5",
                  }}
                >
                  {idx + 1}
                </td>
                {columns.map((col) => (
                  <td
                    key={col.header}
                    style={{
                      textAlign: col.align || "left",
                      fontSize: "11.5px",
                      color: "#1D2939",
                      padding: "6px 8px",
                      borderBottom: idx === rows.length - 1 ? "none" : "1px solid #f0f2f5",
                    }}
                  >
                    {col.cell(row, idx)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ResourceAuditHistoryModal = ({ show, onClose, incidentCategory, severity, groupSeqId }) => {
  const [loader, setLoader] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    const fetchHistory = async () => {
      setLoader(true);
      setError("");
      setHistoryData(null);
      try {
        const url = `${EndPoints.getMatrixHistoryByGroup}?groupSeqId=${encodeURIComponent(groupSeqId || "")}`;
        const resp = await getData(url);
        if (cancelled) return;
        setHistoryData(resp && typeof resp === "object" ? resp : null);
      } catch (e) {
        if (!cancelled) {
          console.error("Error fetching resource audit history:", e);
          setError("Unable to load audit history.");
        }
      } finally {
        if (!cancelled) setLoader(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [show, groupSeqId]);

  // Newest event first, so the most recent change is visible without scrolling.
  const events = useMemo(() => {
    const list = Array.isArray(historyData?.events) ? historyData.events : [];
    return [...list].reverse();
  }, [historyData]);

  // Flattens every event's resource sections (in the order they actually
  // occurred — oldest first, same as the raw API response) into export rows,
  // one row per resource per section, mirroring what each SectionTable shows.
  const buildExportRows = () => {
    const rawEvents = Array.isArray(historyData?.events) ? historyData.events : [];
    const rows = [];

    rawEvents.forEach((event) => {
      const common = {
        "Event ID": showValue(event.eventSeqId || historyData?.groupSeqId),
        Action: getEventMeta(event.action).label,
        "Changed By": showValue(event.changedByName),
        "Changed On": formatDateTime(event.changedOn),
      };

      const resources = Array.isArray(event.resources) ? event.resources : [];
      const changed = Array.isArray(event.changed) ? event.changed : [];
      const added = Array.isArray(event.added) ? event.added : [];
      const removed = Array.isArray(event.removed) ? event.removed : [];
      const unchanged = Array.isArray(event.unchanged) ? event.unchanged : [];

      // "N/A" (not "not applicable to this section") — matches the NA shown
      // on-screen for a null API value, versus a section that genuinely
      // doesn't carry that column (e.g. no Previous/Updated Quantity on a
      // Created row), which also reads NA rather than being left blank.
      const NA = "NA";

      const pushResourceRow = (section, item) => {
        rows.push({
          ...common,
          Section: section,
          Resource: resourceNameOf(item),
          "Available Qty": showValue(item?.cumAvailableQuantity ?? item?.availableQuantity),
          "Damaged Qty": showValue(item?.cumDamagedQuantity),
          "Deployed Qty": showValue(item?.cumDeployedQuantity),
          "Total Qty": showValue(item?.cumTotalQuantity),
          Quantity: showValue(resourceQuantityOf(item)),
          "Previous Quantity": NA,
          "Updated Quantity": NA,
        });
      };

      resources.forEach((r) => pushResourceRow("Created", r));
      added.forEach((r) => pushResourceRow("Added", r));
      removed.forEach((r) => pushResourceRow("Removed", r));
      unchanged.forEach((r) => pushResourceRow("Unchanged", r));

      changed.forEach((c) => {
        getChangeRows(c).forEach((row) => {
          rows.push({
            ...common,
            Section: "Updated",
            Resource: row.label ? `${showValue(c.resourceName)} (${row.label})` : showValue(c.resourceName),
            "Available Qty": showValue(c.cumAvailableQuantity),
            "Damaged Qty": showValue(c.cumDamagedQuantity),
            "Deployed Qty": showValue(c.cumDeployedQuantity),
            "Total Qty": showValue(c.cumTotalQuantity),
            Quantity: showValue(row.to),
            "Previous Quantity": showValue(row.from),
            "Updated Quantity": showValue(row.to),
          });
        });
      });

      if (
        resources.length === 0 &&
        changed.length === 0 &&
        added.length === 0 &&
        removed.length === 0 &&
        unchanged.length === 0
      ) {
        rows.push({
          ...common,
          Section: NA,
          Resource: "No details available",
          "Available Qty": NA,
          "Damaged Qty": NA,
          "Deployed Qty": NA,
          "Total Qty": NA,
          Quantity: NA,
          "Previous Quantity": NA,
          "Updated Quantity": NA,
        });
      }
    });

    return rows;
  };

  const handleExportExcel = async () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info("No audit history to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Resource Audit History");

    const headerRow = ws.addRow(AUDIT_EXPORT_COLUMNS);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E6DA4" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    rows.forEach((r) => {
      const dataRow = ws.addRow(AUDIT_EXPORT_COLUMNS.map((col) => r[col]));
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    ws.columns.forEach((col, idx) => {
      col.width = idx === 5 ? 30 : idx <= 3 ? 20 : 14;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const idPart = historyData?.groupSeqId || groupSeqId || "matrix";
    saveAs(blob, `Resource_Audit_History_${idPart}_${exportFileStamp()}.xlsx`);
  };

  if (!show) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,28,38,0.55)",
        zIndex: 3000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Poppins', 'Segoe UI', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "min(760px, 96vw)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(0,0,0,0.28)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 16px",
            background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryIcon size={14} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "14px", fontWeight: 800, color: "#fff", letterSpacing: ".2px" }}>
              Resource Audit History
            </div>
            <div style={{ fontSize: "10.5px", color: "rgba(255,255,255,0.8)", marginTop: "1px" }}>
              Timeline of changes for the selected incident category &amp; severity
            </div>
          </div>
          <span
            onClick={handleExportExcel}
            title="Download Excel"
            style={{
              cursor: "pointer",
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginRight: "8px",
            }}
          >
            <Sheet size={14} color="#fff" />
          </span>
          <span
            onClick={onClose}
            title="Close"
            style={{
              cursor: "pointer",
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <X size={16} color="#fff" />
          </span>
        </div>

        {/* Context bar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px",
            padding: "10px 16px",
            borderBottom: "1px solid #eef1f4",
            background: "#f8fafc",
          }}
        >
          <span style={{ ...filterLabel, marginRight: "2px" }}>Incident Category</span>
          <span style={{ ...badgeBase, background: "#1d5fa8", color: "#fff", border: "1px solid transparent" }}>
            {showValue(incidentCategory)}
          </span>
          <span style={{ ...filterLabel, margin: "0 2px 0 10px" }}>Severity</span>
          <span style={getSeverityBadgeStyle(severity)}>{showValue(severity)}</span>
          {historyData?.groupSeqId && (
            <>
              <span style={{ ...filterLabel, margin: "0 2px 0 10px" }}>Matrix ID</span>
              <span
                style={{
                  ...badgeBase,
                  background: "#eef2f7",
                  color: "#475569",
                  border: "1px solid #d5e0ea",
                }}
              >
                {historyData.groupSeqId}
              </span>
            </>
          )}
        </div>

        {/* Body */}
        <div style={{ position: "relative", overflowY: "auto", padding: "18px 20px" }}>
          <Loader show={loader} type="body" message="Loading Audit History" />

          {!loader && error && (
            <div style={{ textAlign: "center", color: "#c0392b", fontSize: "12px", padding: "30px 0" }}>
              {error}
            </div>
          )}

          {!loader && !error && events.length === 0 && (
            <div style={{ textAlign: "center", color: "#5b6b7a", fontSize: "12px", padding: "30px 0" }}>
              No audit history found for this record.
            </div>
          )}

          {!loader && !error && events.length > 0 && (
            <div style={{ position: "relative" }}>
              {/* Vertical timeline rail — events are listed newest-first (top),
                  so this line (and the arrow markers along it, below) reads
                  bottom-to-top: oldest event at the bottom, flowing up to the
                  latest one at the top. */}
              <div
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "6px",
                  bottom: "6px",
                  width: "3px",
                  borderRadius: "2px",
                  background: "#c3ccd6",
                }}
              />
              {events.map((event, idx) => {
                const meta = getEventMeta(event.action);
                const Icon = meta.icon;
                const isLatest = idx === 0;
                const resources = Array.isArray(event.resources) ? event.resources : [];
                const changed = Array.isArray(event.changed) ? event.changed : [];
                const added = Array.isArray(event.added) ? event.added : [];
                const removed = Array.isArray(event.removed) ? event.removed : [];
                const unchanged = Array.isArray(event.unchanged) ? event.unchanged : [];

                const isLastEvent = idx === events.length - 1;

                return (
                  <React.Fragment key={event.eventSeqId || `${event.action}-${idx}`}>
                  <div
                    style={{
                      position: "relative",
                      paddingLeft: "42px",
                    }}
                  >
                    {/* Node */}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: meta.bg,
                        border: `2px solid ${meta.color}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                        boxShadow: isLatest ? `0 0 0 4px ${meta.bg}` : "none",
                      }}
                    >
                      <Icon size={14} color={meta.color} />
                    </div>

                    {/* Card */}
                    <div
                      style={{
                        border: `1px solid ${meta.border}`,
                        borderRadius: "10px",
                        overflow: "hidden",
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          background: meta.bg,
                          borderBottom: `1px solid ${meta.border}`,
                        }}
                      >
                        {/* Clean step number instead of the raw event ID (e.g.
                            "SRC-20260923-103-2") — oldest event is Step 1,
                            counting up to the latest at the top of the list. */}
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            background: meta.color,
                            color: "#fff",
                            fontSize: "10.5px",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {events.length - idx}
                        </span>
                        <span
                          style={{
                            ...badgeBase,
                            background: meta.color,
                            color: "#fff",
                            border: "1px solid transparent",
                          }}
                        >
                          {meta.label}
                        </span>
                        {isLatest && (
                          <span
                            style={{
                              ...badgeBase,
                              background: "#fff",
                              color: meta.color,
                              border: `1px solid ${meta.color}`,
                            }}
                          >
                            Latest
                          </span>
                        )}
                        <span style={{ marginLeft: "auto", fontSize: "11.5px", fontWeight: 700, color: meta.color }}>
                          {showValue(event.changedByName)}
                        </span>
                        <span style={{ fontSize: "10.5px", color: "#5b6b7a" }}>
                          {formatDateTime(event.changedOn)}
                        </span>
                      </div>

                      <div style={{ padding: "10px 12px" }}>
                        {/* CREATE: resource list, same card+table shape as the Updated event blocks */}
                        <SectionTable
                          icon={PlusCircle}
                          title="Resources Created"
                          palette={SECTION_PALETTE.added}
                          rows={resources}
                          columns={RESOURCE_DETAIL_COLUMNS}
                        />

                        {/* EDIT/UPDATE: each kind of change gets its own stacked card+table, in a fixed
                            order (quantity changes, then additions, removals, unchanged) so the
                            layout never depends on what happens to be present. */}
                        {(() => {
                          const changeRows = changed.flatMap((c, cIdx) =>
                            getChangeRows(c).map((row, rIdx) => ({
                              ...row,
                              resourceName: c.resourceName,
                              cumAvailableQuantity: c.cumAvailableQuantity,
                              cumDamagedQuantity: c.cumDamagedQuantity,
                              cumDeployedQuantity: c.cumDeployedQuantity,
                              cumTotalQuantity: c.cumTotalQuantity,
                              cIdx,
                              rIdx,
                            }))
                          );
                          return (
                            <>
                              <SectionTable
                                icon={RefreshCcw}
                                title="Resources Updated"
                                palette={SECTION_PALETTE.changes}
                                rows={changeRows}
                                rowKey={(row) => `${row.cIdx}-${row.rIdx}`}
                                columns={[
                                  {
                                    header: "Resource",
                                    cell: (row) => (
                                      <>
                                        <div style={{ fontWeight: 600 }}>{showValue(row.resourceName)}</div>
                                        {row.label && (
                                          <div style={{ fontSize: "10px", color: "#8a97a3", marginTop: "1px" }}>
                                            {row.label}
                                          </div>
                                        )}
                                      </>
                                    ),
                                  },
                                  {
                                    header: "Available Qty",
                                    align: "center",
                                    cell: (row) => showValue(row.cumAvailableQuantity),
                                  },
                                  {
                                    header: "Damaged Qty",
                                    align: "center",
                                    cell: (row) => showValue(row.cumDamagedQuantity),
                                  },
                                  {
                                    header: "Deployed Qty",
                                    align: "center",
                                    cell: (row) => showValue(row.cumDeployedQuantity),
                                  },
                                  {
                                    header: "Total Qty",
                                    align: "center",
                                    cell: (row) => showValue(row.cumTotalQuantity),
                                  },
                                  {
                                    header: "Previous Quantity",
                                    align: "center",
                                    cell: (row) => showValue(row.from),
                                  },
                                  {
                                    header: "Updated Quantity",
                                    align: "center",
                                    cell: (row) => showValue(row.to),
                                  },
                                ]}
                              />

                              <SectionTable
                                icon={PlusCircle}
                                title="Resources Added"
                                palette={SECTION_PALETTE.added}
                                rows={added}
                                columns={RESOURCE_DETAIL_COLUMNS}
                              />

                              <SectionTable
                                icon={Trash2}
                                title="Resources Removed"
                                palette={SECTION_PALETTE.removed}
                                rows={removed}
                                columns={RESOURCE_DETAIL_COLUMNS}
                              />

                              <SectionTable
                                icon={Repeat}
                                title="Unchanged Resources"
                                palette={SECTION_PALETTE.unchanged}
                                rows={unchanged}
                                columns={RESOURCE_DETAIL_COLUMNS}
                              />
                            </>
                          );
                        })()}

                        {resources.length === 0 &&
                          changed.length === 0 &&
                          added.length === 0 &&
                          removed.length === 0 &&
                          unchanged.length === 0 && (
                            <div style={{ fontSize: "11px", color: "#8a97a3" }}>No details available.</div>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Connector arrow — points up the rail toward the next (newer)
                      event, so the timeline direction (oldest at the bottom,
                      latest at the top) is unambiguous. */}
                  {!isLastEvent && (
                    <div style={{ position: "relative", height: "34px" }}>
                      <div
                        title="Older → Newer"
                        style={{
                          position: "absolute",
                          left: "3px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          background: "#fff",
                          border: "2px solid #9aa8b6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 2,
                        }}
                      >
                        <ChevronUp size={18} color="#5b6b7a" strokeWidth={3} />
                      </div>
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid #eef1f4",
            display: "flex",
            justifyContent: "flex-end",
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              height: "32px",
              fontSize: "12px",
              fontWeight: 600,
              padding: "0 18px",
              borderRadius: "6px",
              cursor: "pointer",
              border: "1px solid #2E6DA4",
              color: "#fff",
              background: "#2E6DA4",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResourceAuditHistoryModal;
